import type {
  IDashboardReportChartSeries,
  IDashboardReportMetrics,
  IDashboardReportParams,
  IDashboardReportSearch,
} from '@/interfaces/api/dashboard-report'
import type { IShift } from '@/interfaces/entities/shifts'
import type {
  IDashboardSearch,
  IGroupTotalizer,
  ISensorTotalizer,
} from '@/interfaces/stores/dashboard'
import {
  DASHBOARD_SEARCH_COLOR_PALETTE,
  formatDashboardPeriodRangeLabel,
  normalizeDashboardPeriod,
} from '@/utils/dashboard-workspace'

function toIso(date: Date): string {
  return date.toISOString()
}

function resolveShiftName(shiftId: number | null, shifts: readonly IShift[]): string | null {
  if (shiftId == null) return null
  return shifts.find((s) => s.id === shiftId)?.name ?? null
}

function buildSensorMetrics(totalizer: ISensorTotalizer): IDashboardReportMetrics {
  const { consumption, demand, utilization } = totalizer
  return {
    total_consumption_kwh: Number(consumption?.total_consumption_kwh ?? 0),
    peak_consumption_kwh: Number(consumption?.peak_consumption_kwh ?? 0),
    off_peak_consumption_kwh: Number(consumption?.off_peak_consumption_kwh ?? 0),
    peak_demand_kw: Number(demand?.peak_demand_kw ?? 0),
    off_peak_demand_kw: Number(demand?.off_peak_demand_kw ?? 0),
    utilization: Number(utilization?.utilization ?? 0),
    total_utilization_time: Number(utilization?.total_utilization_time ?? 0),
    total_non_utilization_time: Number(utilization?.total_non_utilization_time ?? 0),
  }
}

function buildGroupMetrics(totalizer: IGroupTotalizer): IDashboardReportMetrics {
  return {
    total_consumption_kwh: Number(totalizer.total_consumption_kwh ?? 0),
    peak_consumption_kwh: Number(totalizer.peak_consumption_kwh ?? 0),
    off_peak_consumption_kwh: Number(totalizer.off_peak_consumption_kwh ?? 0),
    peak_demand_kw: Number(totalizer.peak_demand_max ?? 0),
    off_peak_demand_kw: Number(totalizer.off_peak_demand_max ?? 0),
    utilization: Number(totalizer.utilization_percentage ?? 0),
    total_utilization_time: Number(totalizer.total_utilization_time ?? 0),
    total_non_utilization_time: Number(totalizer.total_non_utilization_time ?? 0),
  }
}

function readingTimeToIso(time: string | Date): string {
  if (time instanceof Date) return time.toISOString()
  const parsed = new Date(time)
  return Number.isNaN(parsed.getTime()) ? String(time) : parsed.toISOString()
}

function buildSensorChartSeries(search: IDashboardSearch): IDashboardReportChartSeries[] {
  const totalizer = search.totalizer as ISensorTotalizer
  const readings = totalizer.readings ?? []
  if (!readings.length) return []

  return [
    {
      name: search.label,
      color: search.color,
      points: readings.map((r) => ({
        time: readingTimeToIso(r.time),
        value: Number(r.total_power),
      })),
    },
  ]
}

function seriesColorForIndex(searchColor: string, index: number): string {
  if (index === 0) return searchColor
  return DASHBOARD_SEARCH_COLOR_PALETTE[index % DASHBOARD_SEARCH_COLOR_PALETTE.length]
}

function buildGroupChartSeries(search: IDashboardSearch): IDashboardReportChartSeries[] {
  const totalizer = search.totalizer as IGroupTotalizer
  const sensorsReadings = totalizer.energy_sensors_readings ?? []
  if (!sensorsReadings.length) return []

  return sensorsReadings.map((sensorReading, index) => ({
    name: sensorReading.sensor_name,
    color: seriesColorForIndex(search.color, index),
    points: (sensorReading.readings ?? []).map((r) => ({
      time: readingTimeToIso(r.time),
      value: Number(r.total_power),
    })),
  }))
}

function mapSearchToReportItem(search: IDashboardSearch, shifts: readonly IShift[]): IDashboardReportSearch {
  const periodDates = normalizeDashboardPeriod(search.period)
  const start = periodDates[0]
  const end = periodDates[1]

  const metrics =
    search.type === 'sensor'
      ? buildSensorMetrics(search.totalizer as ISensorTotalizer)
      : buildGroupMetrics(search.totalizer as IGroupTotalizer)

  const chart_series =
    search.type === 'sensor' ? buildSensorChartSeries(search) : buildGroupChartSeries(search)

  return {
    type: search.type,
    label: search.label,
    color: search.color,
    period_label: formatDashboardPeriodRangeLabel(search.period),
    start_date: start ? toIso(start) : '',
    end_date: end ? toIso(end) : '',
    shift_id: search.shiftId,
    shift_name: resolveShiftName(search.shiftId, shifts),
    metrics,
    chart_series,
  }
}

export interface IBuildDashboardReportPayloadOptions {
  visibleSearches: IDashboardSearch[]
  companyName: string
  shifts: readonly IShift[]
  generatedAt?: string
}

export function buildDashboardReportPayload(
  options: IBuildDashboardReportPayloadOptions,
): IDashboardReportParams {
  const { visibleSearches, companyName, shifts, generatedAt } = options

  return {
    company_name: companyName || 'Empresa',
    generated_at: generatedAt ?? new Date().toISOString(),
    searches: visibleSearches.map((search) => mapSearchToReportItem(search, shifts)),
  }
}
